import React, { useCallback, useState } from 'react';
import { cn } from '../../utils/cn';
import { FolderNode } from '../../../core/types/asset.types';
import { useProjectPanelStore } from '../../store/projectPanelStore';
import { Folder } from 'lucide-react';

interface FolderRowItemProps {
  folder: FolderNode;
}

export const FolderRowItem: React.FC<FolderRowItemProps> = ({ folder }) => {
  const { selectFolder, toggleFolderExpanded, draggedAssetId, moveAsset } = useProjectPanelStore();
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDoubleClick = useCallback(() => {
    selectFolder(folder.id);
    if (!folder.expanded) {
      toggleFolderExpanded(folder.id);
    }
  }, [folder.id, folder.expanded, selectFolder, toggleFolderExpanded]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    if (draggedAssetId) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setIsDragOver(true);
    }
  }, [draggedAssetId]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    // Don't stopPropagation — let the parent's handleDrop also fire to clear isDragOver
    setIsDragOver(false);

    if (draggedAssetId) {
      moveAsset(draggedAssetId, folder.id);
    }
  }, [draggedAssetId, folder.id, moveAsset]);

  return (
    <div
      onDoubleClick={handleDoubleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'flex items-center px-2 py-1.5 border-b border-zinc-800 text-xs cursor-pointer hover:bg-zinc-700/50',
        isDragOver && 'bg-blue-600/30 ring-2 ring-blue-500 ring-dashed'
      )}
    >
      <div className="flex-1 min-w-[200px] flex items-center gap-2 truncate">
        <Folder size={14} className={cn('flex-shrink-0', isDragOver ? 'text-blue-400' : 'text-yellow-500/80')} />
        <span className="truncate text-zinc-300">{folder.name}</span>
      </div>
      <div className="w-24 text-zinc-500">Folder</div>
      <div className="w-20 text-right text-zinc-500">—</div>
      <div className="w-24 text-right text-zinc-500">—</div>
    </div>
  );
};
